import type { GameData, LeagueGames } from '@library/types/games';
import { DateTime } from 'luxon';
import type { LeaguesResponse } from '@library/types/leagues';
import { api } from '@bo-services/api';
import { apiPaths } from '@bo-constants/services';
import { getLeagueGames } from '@bo-utils/leagueGames';

const { admin } = apiPaths;

export const leaguesApi = api.injectEndpoints({
  endpoints: (build) => ({
    getAllLeagues: build.query<LeaguesResponse, void>({
      query: () => ({
        url: `${admin}leagues`,
        method: 'GET',
        params: { skip_projections_count: true }
      })
    }),
    getLeagueGames: build.query<
      LeagueGames,
      { fromDate?: string; leagueId?: string; toDate?: string }
    >({
      query: ({ leagueId, fromDate, toDate }) => ({
        url: `${admin}load_games`,
        method: 'GET',
        params: {
          league_id: leagueId,
          from_date: fromDate,
          to_date: fromDate
            ? DateTime.fromISO(toDate ?? fromDate)
                .plus({ days: 1 })
                .toJSON() // this is needed to find all the games that flows into next UTC date
            : ''
        },
        headers: {
          Accept: 'application/json',
          'Content-type': 'application/json; charset=UTF-8'
        }
      }),
      providesTags: ['Games'],
      transformResponse: (response: LeagueGames, _meta, arg) =>
        getLeagueGames({
          fromDateTime: arg.fromDate ?? DateTime.now().toISO(),
          leagueGames: response,
          toDateTime: arg.toDate ?? DateTime.now().plus({ days: 10 }).toISO()
        })
    }),
    getLeagueGame: build.query<GameData, { gameId: string; leagueId: string }>({
      query: ({ gameId, leagueId }) => ({
        url: `${admin}games/${gameId}`,
        method: 'GET',
        params: {
          league_id: leagueId
        },
        headers: {
          Accept: 'application/json',
          'Content-type': 'application/json; charset=UTF-8'
        }
      })
    })
  })
});

export const {
  useGetAllLeaguesQuery,
  useGetLeagueGamesQuery,
  useGetLeagueGameQuery
} = leaguesApi;
